// UTM / acquisition capture — first-touch, read-once, 30-day persistence.
// Stored in BOTH localStorage and a cookie (cross-subdomain on the apex domain).
import Cookies from 'js-cookie';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'fbclid',
];
const STORAGE_KEY = 'dietbanao_utm';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const TTL_DAYS = 30;

function _cookieDomain() {
  try {
    const h = window.location.hostname || '';
    // Cross-subdomain only on the production apex; host-only elsewhere (previews/localhost).
    return h.endsWith('dietbanao.com') ? '.dietbanao.com' : undefined;
  } catch {
    return undefined;
  }
}

function _readRaw() {
  try {
    const fromCookie = Cookies.get(STORAGE_KEY);
    if (fromCookie) return JSON.parse(fromCookie);
  } catch {
    /* ignore */
  }
  try {
    const fromLs = localStorage.getItem(STORAGE_KEY);
    if (fromLs) return JSON.parse(fromLs);
  } catch {
    /* ignore */
  }
  return null;
}

function _write(record) {
  const json = JSON.stringify(record);
  try { localStorage.setItem(STORAGE_KEY, json); } catch { /* ignore */ }
  try { Cookies.set(STORAGE_KEY, json, { expires: TTL_DAYS, sameSite: 'Lax', domain: _cookieDomain() }); } catch { /* ignore */ }
}

// First-touch: capture and never overwrite a real CAMPAIGN first-touch. An
// organic/direct landing (no UTMs) is recorded too, but is "upgraded" the first
// time a campaign-tagged visit arrives — that click is the first real source
// touch, so we capture it rather than lose the attribution. A campaign source,
// once stored, is never overwritten by a later visit. Always stored in
// localStorage + cookie (never sessionStorage) so it survives tab close.
export function captureUTM() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v.slice(0, 200);
    });
    const hasUtmNow = Object.keys(found).length > 0;
    const hasSourceNow = !!found.utm_source;

    const existing = _readRaw();
    const existingFresh = !!existing && Date.now() - (existing.captured_at || 0) <= TTL_MS;
    // LOCK BOUNDARY: a stored entry with a real `utm_source` is the first
    // campaign touch and is LOCKED — a second campaign click never upgrades
    // over it. (gclid/fbclid-only and organic entries are NOT locked.)
    const existingLocked = !!existing && !!existing.utm_source;

    // 1) Real campaign first-touch already on record → never overwrite.
    if (existingFresh && existingLocked) return;
    // 2) Existing organic/direct (or click-id-only) first-touch is upgraded ONLY
    //    when THIS visit carries a utm_source (the first real source touch).
    //    Otherwise keep the existing organic record untouched.
    if (existingFresh && !hasSourceNow) return;

    const record = {
      ...found,
      landing_page: (window.location.href || '').slice(0, 500),
      referrer: (document.referrer || '').slice(0, 500),
      captured_at: Date.now(),
    };
    _write(record);
  } catch {
    // never break navigation
  }
}

// Compact UTM set for EVENT enrichment (no landing_page/referrer noise).
export function getStoredUTM() {
  const data = _readRaw();
  if (!data || Date.now() - (data.captured_at || 0) > TTL_MS) return {};
  const utm = {};
  UTM_KEYS.forEach((k) => { if (data[k]) utm[k] = data[k]; });
  return utm;
}

// Full acquisition object for BACKEND persistence (user / drop-off / order).
export function getAcquisition() {
  const data = _readRaw();
  if (!data || Date.now() - (data.captured_at || 0) > TTL_MS) return {};
  const out = {};
  UTM_KEYS.forEach((k) => { if (data[k]) out[k] = data[k]; });
  if (data.landing_page) out.landing_page = data.landing_page;
  if (data.referrer) out.referrer = data.referrer;
  out.first_touch_at = new Date(data.captured_at || Date.now()).toISOString();
  return out;
}

export function clearUTM() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  try { Cookies.remove(STORAGE_KEY, { domain: _cookieDomain() }); } catch { /* ignore */ }
}
